// Config partagée d'affichage du centre de notifications (WKF-013).

import { formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import {
    ChevronsUp,
    ChevronUp,
    ClipboardList,
    Forward,
    Minus,
    TrendingUp,
    Zap,
    type LucideIcon,
} from 'lucide-react'

import type { Priority, SourceType } from '@/hooks/useNotifications.types'

export const SOURCE_ICON: Record<SourceType, LucideIcon> = {
    workflow: Zap,
    escalation: TrendingUp,
    transfer: Forward,
    manual: ClipboardList,
}

/**
 * Pastille de la source — dynamise la ligne sans multiplier les couleurs
 * (retour PO « trop vide »). 3 registres sémantiques, pas 4 couleurs arbitraires :
 * tâches (teal discret) / alerte info (neutre) / escalade (warning = attention).
 */
export const SOURCE_PASTILLE: Record<SourceType, string> = {
    manual: 'bg-primary/10 text-primary',
    transfer: 'bg-primary/10 text-primary',
    workflow: 'bg-muted text-muted-foreground',
    escalation: 'bg-warning-bg text-warning',
}

/**
 * Priorité (3 niveaux — arbitrage PO 2026-07-05 : Normale + les deux au-dessus).
 * A11y daltonien : la reconnaissance passe par la FORME de l'icône (plate →
 * chevron → double chevron) EN PLUS de la couleur (vert → orange → rouge).
 * Toujours doublée du libellé texte.
 */
// Couleurs = tokens sémantiques du Design System (jamais emerald/amber hors palette :
// DS §1.3.bis « un seul vert = le teal primary »). NORMAL reste NEUTRE (baseline,
// pas de couleur) : la couleur est un signal réservé à HIGH (warning) / URGENT (destructive).
export const PRIORITY_META: Record<Priority, { icon: LucideIcon; className: string; labelKey: string }> = {
    NORMAL: { icon: Minus, className: 'text-muted-foreground', labelKey: 'notifications.priority.normal' },
    HIGH: { icon: ChevronUp, className: 'text-warning', labelKey: 'notifications.priority.high' },
    URGENT: { icon: ChevronsUp, className: 'text-destructive', labelKey: 'notifications.priority.urgent' },
}

export const PRIORITY_LABEL_KEY: Record<Priority, string> = {
    NORMAL: 'notifications.priority.normal',
    HIGH: 'notifications.priority.high',
    URGENT: 'notifications.priority.urgent',
}

/**
 * Chips de priorité colorés (retour PO 2026-07-07 : préférés aux chevrons).
 * Tokens sémantiques DS EXCLUSIVEMENT (jamais de vert/emerald — DS §1.3.bis
 * « un seul vert = le teal primary »). Le texte accompagne TOUJOURS la couleur
 * (a11y daltonien : la couleur n'est jamais le seul vecteur).
 * NORMAL = null : baseline sans chip en liste (anti-bruit) ; le sélecteur de la
 * modale l'affiche via `showNormal` (variante secondary neutre).
 */
export const PRIORITY_CHIP: Record<Priority, { className: string; labelKey: string } | null> = {
    NORMAL: null,
    HIGH: {
        className: 'bg-warning-bg text-warning border border-warning/30',
        labelKey: 'notifications.priority.high',
    },
    URGENT: {
        className: 'bg-destructive-bg text-destructive border border-destructive/30',
        labelKey: 'notifications.priority.urgent',
    },
}

/** Variante neutre pour NORMAL (utilisée seulement par le sélecteur de la modale). */
export const PRIORITY_CHIP_NORMAL = {
    className: 'bg-secondary text-secondary-foreground border border-transparent',
    labelKey: 'notifications.priority.normal',
}

/** Temps relatif localisé (« il y a 2 h » / « 2 hours ago »). */
export function relativeTime(iso: string | null, lang: string): string {
    if (!iso) return ''
    try {
        return formatDistanceToNow(new Date(iso), {
            addSuffix: true,
            locale: lang.startsWith('en') ? enUS : fr,
        })
    } catch {
        return ''
    }
}

/** Statuts « clôturés » (à afficher sous « Terminées récemment »). */
export const CLOSED_STATUSES = new Set(['TREATED', 'AUTO_RESOLVED'])
